// User lifecycle hooks — deactivate / reactivate / hard delete.
//
// Deactivate is reversible: bans the Clerk user (revokes all sessions, blocks
// future sign-in) and sets users.active=false. Reactivate undoes both.
//
// Delete is irreversible: removes the Clerk user and the local users row
// (cascades clear child data; alarms/audit-log keep the row with actor_id=NULL).
//
// Both refuse to operate on the caller's own user_id. Delete additionally
// refuses while the target has an active duress alarm.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

interface LifecycleResponse {
  user: { user_id: string; active: boolean };
}

interface DeleteResponse {
  deleted: { user_id: string; email: string };
}

export function useDeactivateUser() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api<LifecycleResponse>(`/users/${userId}/deactivate`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roster'] });
    },
  });
}

export function useReactivateUser() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api<LifecycleResponse>(`/users/${userId}/reactivate`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roster'] });
    },
  });
}

export function useDeleteUser() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api<DeleteResponse>(`/users/${userId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roster'] });
      qc.invalidateQueries({ queryKey: ['invitations'] });
    },
  });
}
